import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import FilterSidebar from './components/FilterSidebar';
import SearchHeader from './components/SearchHeader';
import GiftCard from './components/GiftCard';
import ComparisonPanel from './components/ComparisonPanel';
import WishlistSelectionModal from '../../components/WishlistSelectionModal';
import SocialShare from '../../components/ui/SocialShare';
import SmartQueryBuilder from '../../components/SmartQueryBuilder';
import { productsService } from '../../services/products';
import { wishlistsService } from '../../services/wishlists';
import {
  saveToContact,
  markAsPurchased,
  getUserContacts,
  getPurchaseHistory
} from '../../services/userActions';

import { contactsService } from '../../services/contacts';
import { useAuth } from '../../contexts/AuthContext';

// Map frontend sort values to backend parameters
const mapSortToBackend = (frontendSort) => {
  const sortMapping = {
    'relevance': '', // Default - no ordering parameter
    'price-low': 'retail_price',
    'price-high': '-retail_price',
    'popularity': '-created_at', // Approximate with newest
    'success-rate': '-total_relevance_score', // Use enhanced gift search relevance score
    'gift-score': '-total_relevance_score', // Alternative name for gift success rate
    'newest': '-created_at'
  };
  return sortMapping[frontendSort] || '';
};

// Handle different pagination formats from different endpoints
const handlePaginationResponse = (data) => {
  if (data.count !== undefined) {
    // Standard Django pagination (products endpoint)
    return {
      results: data.results,
      totalCount: data.count,
      hasNext: !!data.next,
      hasPrevious: !!data.previous,
      nextUrl: data.next,
      previousUrl: data.previous
    };
  } else if (data.metadata) {
    // Custom search pagination format
    return {
      results: data.results,
      totalCount: data.metadata.total_results || 0,
      hasNext: data.metadata.has_next || false,
      hasPrevious: data.metadata.has_previous || false,
      stage: data.stage
    };
  }
  return { results: data.results || [] };
};

// Map frontend filter values to backend API parameters
const mapFiltersToAPI = (filters) => {
  const apiParams = {};

  // Direct parameter mappings (these work according to action plan)
  if (filters.search) apiParams.search = filters.search;
  if (filters.category) apiParams.category = filters.category;
  if (filters.min_price) apiParams.min_price = filters.min_price;
  if (filters.max_price) apiParams.max_price = filters.max_price;
  if (filters.ordering) apiParams.ordering = filters.ordering;
  if (filters.page) apiParams.page = filters.page;

  // Working boolean filters
  if (filters.is_featured) apiParams.is_featured = filters.is_featured;
  if (filters.is_new) apiParams.is_new = filters.is_new;
  if (filters.is_sale) apiParams.is_sale = filters.is_sale;
  if (filters.is_top_seller) apiParams.is_top_seller = filters.is_top_seller;

  // Working string filters
  if (filters.occasion) apiParams.occasion = filters.occasion;
  if (filters.relationship) apiParams.relationship = filters.relationship;

  // Map frontend filter names to backend parameter names
  if (filters.gender) {
    apiParams.gender_suitability = filters.gender;
  }
  if (filters.ageRange) {
    apiParams.age_range = filters.ageRange;
  }

  // Budget mapping
  if (filters.budget) {
    if (filters.budget === 'custom' && filters.customBudget) {
      if (filters.customBudget.min) apiParams.min_price = filters.customBudget.min;
      if (filters.customBudget.max) apiParams.max_price = filters.customBudget.max;
    } else if (filters.budget !== 'custom') {
      // Map budget ranges to min/max prices
      const budgetRanges = {
        'under-25': { max: 25 },
        '25-50': { min: 25, max: 50 },
        '50-100': { min: 50, max: 100 },
        '100-200': { min: 100, max: 200 },
        '200-500': { min: 200, max: 500 },
        'over-500': { min: 500 }
      };

      const range = budgetRanges[filters.budget];
      if (range) {
        if (range.min) apiParams.min_price = range.min;
        if (range.max) apiParams.max_price = range.max;
      }
    }
  }

  // Handle interests arrays (if they should be passed directly to API)
  if (filters.interests && filters.interests.length > 0) {
    apiParams.interests = filters.interests.join(',');
  }

  if (filters.negativeInterests && filters.negativeInterests.length > 0) {
    apiParams.negative_interests = filters.negativeInterests.join(',');
  }

  return apiParams;
};

const GiftFinder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchStage, setSearchStage] = useState(null);
  const searchTimeoutRef = useRef(null);
  const lastScrollY = useRef(0);

  const [filters, setFilters] = useState({
    relationship: '',
    gender: '',
    ageRange: '',
    category: '',
    budget: '',
    customBudget: { min: '', max: '' },
    occasion: '',
    interests: [],
    negativeInterests: []
  });

  // Pending filters that haven't been applied yet
  const [pendingFilters, setPendingFilters] = useState({
    relationship: '',
    gender: '',
    ageRange: '',
    category: '',
    budget: '',
    customBudget: { min: '', max: '' },
    occasion: '',
    interests: [],
    negativeInterests: []
  });

  // Track if there are unapplied filter changes
  const [hasUnappliedFilters, setHasUnappliedFilters] = useState(false);

  // Initialize pending filters from URL on component mount
  useEffect(() => {
    setPendingFilters(filters);
  }, []); // Only run on mount

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [savedGifts, setSavedGifts] = useState([]);
  const [comparisonItems, setComparisonItems] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [userWishlists, setUserWishlists] = useState([]);
  const [defaultWishlist, setDefaultWishlist] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Smart Query Builder state
  const [showSmartQuery, setShowSmartQuery] = useState(false);
  const [smartQueryValue, setSmartQueryValue] = useState('');

  // Enhanced functionality state
  const [userContacts, setUserContacts] = useState([]);
  const [userPurchases, setUserPurchases] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [enableGiftFeatures, setEnableGiftFeatures] = useState(true);

  // Wishlist modal state
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [selectedProductForWishlist, setSelectedProductForWishlist] = useState(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedProductForShare, setSelectedProductForShare] = useState(null);

  // Handle search query from location state (from homepage navigation)
  useEffect(() => {
    if (location.state?.query && location.state.query.trim()) {
      const query = location.state.query.trim();

      // Update URL parameters to include the search query
      const newParams = new URLSearchParams(searchParams);
      newParams.set('search', query);
      setSearchParams(newParams);

      // Clear the location state to prevent repeated execution
      window.history.replaceState({}, document.title);
    }
  }, [location.state, searchParams, setSearchParams]);

  // Initialize smart query value from URL
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    if (searchFromUrl && searchFromUrl !== smartQueryValue) {
      setSmartQueryValue(searchFromUrl);
      setSearchQuery(searchFromUrl);
    }
  }, [searchParams]);

  // Extract current filters from URL parameters (memoized to prevent infinite re-renders)
  const currentFilters = useMemo(() => ({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    ordering: searchParams.get('ordering') || '',
    page: searchParams.get('page') || 1,
    // Additional filter parameters identified from backend API
    is_featured: searchParams.get('is_featured') || '',
    is_new: searchParams.get('is_new') || '',
    is_sale: searchParams.get('is_sale') || '',
    is_top_seller: searchParams.get('is_top_seller') || '',
    occasion: searchParams.get('occasion') || '',
    gender: searchParams.get('gender') || '',
    relationship: searchParams.get('relationship') || '',
    age_range: searchParams.get('age_range') || ''
  }), [searchParams]);

  // Load user wishlists and create default if needed
  const loadUserWishlists = useCallback(async () => {
    if (!isAuthenticated) {
      setUserWishlists([]);
      setDefaultWishlist(null);
      return;
    }

    try {
      const wishlists = await wishlistsService.getWishlists();
      setUserWishlists(wishlists);

      // Find or create a default wishlist
      let defaultList = wishlists.find(w => w.name === 'My Wishlist' || w.name === 'Favorites');

      if (!defaultList && wishlists.length === 0) {
        // Create a default wishlist if user has none
        defaultList = await wishlistsService.createWishlist({
          name: 'My Wishlist',
          description: 'My favorite gifts from Gift Finder'
        });
        setUserWishlists([defaultList]);
      } else if (!defaultList && wishlists.length > 0) {
        // Use the first wishlist as default
        defaultList = wishlists[0];
      }

      setDefaultWishlist(defaultList);

      // Load saved gifts from ALL wishlists (not just default)
      const allSavedIds = new Set();
      wishlists.forEach(wishlist => {
        if (wishlist.items) {
          wishlist.items.forEach(item => {
            if (item.product?.id) {
              allSavedIds.add(item.product.id);
            }
          });
        }
      });
      setSavedGifts(Array.from(allSavedIds));

    } catch (error) {
      console.error('Error loading user wishlists:', error);
      // Don't show error to user for wishlist loading - it's not critical
    }
  }, [isAuthenticated]);

  // Load user wishlists when authentication state changes
  useEffect(() => {
    loadUserWishlists();
  }, [loadUserWishlists]);

  // Load enhanced user data
  const loadUserContacts = useCallback(async () => {
    if (!isAuthenticated) {
      setUserContacts([]);
      return;
    }

    try {
      const contacts = await getUserContacts();
      setUserContacts(contacts.results || contacts);
    } catch (error) {
      console.error('Error loading user contacts:', error);
    }
  }, [isAuthenticated]);

  const loadUserPurchases = useCallback(async () => {
    if (!isAuthenticated) {
      setUserPurchases([]);
      return;
    }

    try {
      const purchases = await getPurchaseHistory();
      const purchaseIds = purchases.results ?
        purchases.results.map(p => p.product_id || p.product?.id) :
        purchases.map(p => p.product_id || p.product?.id);
      setUserPurchases(purchaseIds.filter(Boolean));
    } catch (error) {
      console.error('Error loading user purchases:', error);
    }
  }, [isAuthenticated]);

  // Load enhanced data when authentication state changes
  useEffect(() => {
    loadUserContacts();
    loadUserPurchases();
  }, [loadUserContacts, loadUserPurchases]);

  // Helper function to enhance search query with interests
  const enhanceSearchQuery = (baseQuery, interests, negativeInterests) => {
    let enhancedQuery = baseQuery || 'gifts';

    // Add positive interests
    if (interests && interests.length > 0) {
      const interestList = interests.join(', ');
      if (interests.length === 1) {
        enhancedQuery += ` for someone who loves ${interestList}`;
      } else {
        enhancedQuery += ` for someone who loves ${interestList}`;
      }
    }

    // Add negative interests
    if (negativeInterests && negativeInterests.length > 0) {
      const negativeList = negativeInterests.join(', ');
      if (negativeInterests.length === 1) {
        enhancedQuery += ` but avoids ${negativeList}`;
      } else {
        enhancedQuery += ` but avoids ${negativeList}`;
      }
    }

    return enhancedQuery;
  };

  // Handle smart query builder changes
  const handleSmartQueryChange = (query) => {
    setSmartQueryValue(query);
    setSearchQuery(query);

    // Update the URL parameters using the existing filter system
    handleFilterChange({ search: query });
  };

  // Load products from API
  const loadProducts = async () => {
    try {
      // Reset infinite scroll states for new search/filter
      setLoadingMore(false);
      setHasNextPage(false);
      setCurrentPage(1);
      const baseSearchQuery = currentFilters.search && currentFilters.search.trim();
      const hasInterests = (filters.interests && filters.interests.length > 0) ||
        (filters.negativeInterests && filters.negativeInterests.length > 0);

      // Enhance search query with interests if available
      const enhancedQuery = enhanceSearchQuery(
        baseSearchQuery,
        filters.interests,
        filters.negativeInterests
      );

      const isSearchQuery = enhancedQuery && enhancedQuery.trim() && enhancedQuery !== 'gifts';

      // Debug logging
      console.log('🔎 Query Debug:');
      console.log('  baseSearchQuery:', baseSearchQuery);
      console.log('  enhancedQuery:', enhancedQuery);
      console.log('  isSearchQuery:', isSearchQuery);
      console.log('  Will use:', isSearchQuery ? 'searchProducts' : 'getProducts');

      // Set appropriate loading states
      if (isSearchQuery) {
        setSearchLoading(true);
        if (hasInterests) {
          setSearchStage('Analyzing preferences with AI...');
        } else {
          setSearchStage('Analyzing your request...');
        }
      } else {
        setLoading(true);
      }

      setError(null);

      // Clean up the filters object to remove empty values
      const cleanFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([key, value]) => {
          // Remove empty strings
          if (value === '' || value === null || value === undefined) return false;

          // Remove empty arrays
          if (Array.isArray(value) && value.length === 0) return false;

          // Remove empty customBudget objects
          if (key === 'customBudget' && typeof value === 'object') {
            return value.min || value.max;
          }

          return true;
        })
      );

      // Apply sort parameter mapping
      if (cleanFilters.ordering) {
        cleanFilters.ordering = mapSortToBackend(cleanFilters.ordering);
      }

      // Map filters to proper API parameters
      const apiFilters = mapFiltersToAPI(cleanFilters);

      // Debug logging
      console.log('🔍 Filter Debug Info:');
      console.log('  currentFilters:', currentFilters);
      console.log('  cleanFilters:', cleanFilters);
      console.log('  apiFilters:', apiFilters);

      let data;
      let paginationData;

      // IMPROVED SEARCH STRATEGY: Single call with proper loading states
      if (isSearchQuery) {
        console.log('🔍 Taking SEARCH path');
        // Update search stage
        if (hasInterests) {
          setSearchStage('Finding perfect matches with AI...');
        } else {
          setSearchStage('Finding perfect gifts with AI...');
        }

        // Use enhanced search query for AI-powered query parsing
        const searchQuery = enhancedQuery;
        const otherFilters = { ...apiFilters };
        delete otherFilters.search; // Remove search from other filters

        console.log('📡 Making searchProducts API call with query:', searchQuery, 'filters:', otherFilters);
        data = await productsService.searchProducts(searchQuery, otherFilters);
        console.log('📦 searchProducts response:', data);
        paginationData = handlePaginationResponse(data);

        // Update search stage based on response
        if (data.stage) {
          setSearchStage(data.stage === 'enhanced' ? 'AI analysis complete!' : 'Processing...');
        }
      } else {
        console.log('🎯 Taking FILTER path');
        // Use regular product listing with filters (CORRECT endpoint for filtering)
        console.log('📡 Making getProducts API call with filters:', apiFilters);
        data = await productsService.getProducts(apiFilters);
        console.log('📦 getProducts response:', data);
        paginationData = handlePaginationResponse(data);
      }

      const productsToSet = paginationData.results || data.results || data;

      // Always set products and pagination
      setProducts(productsToSet || []);

      // Set pagination based on the processed response
      if (paginationData.totalCount !== undefined) {
        setPagination({
          count: paginationData.totalCount,
          next: paginationData.hasNext ? paginationData.nextUrl : null,
          previous: paginationData.hasPrevious ? paginationData.previousUrl : null
        });
        setHasNextPage(paginationData.hasNext || false);
      } else if (data.count !== undefined) {
        setPagination({
          count: data.count,
          next: data.next,
          previous: data.previous
        });
        setHasNextPage(!!data.next);
      } else {
        // Fallback: use the actual products length as count
        setPagination({
          count: productsToSet ? productsToSet.length : 0,
          next: null,
          previous: null
        });
        setHasNextPage(false);
      }

    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('🚨 API Error Details:');
      console.error('  Error:', err);
      console.error('  Status:', err.response?.status);
      console.error('  Data:', err.response?.data);
      console.error('  URL that failed:', err.config?.url);
    } finally {
      setLoading(false);
      setSearchLoading(false);
      setSearchStage(null);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    try {
      const data = await productsService.getCategories();
      setCategories(data.results || data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };



  // Transform product data to match the expected gift format
  const transformProductToGift = (product) => ({
    id: product.id,
    name: product.name || product.title,
    brand: product.brand,
    price: product.retail_price || product.price,
    retail_price: product.retail_price || product.price,
    discount_price: product.discount_price,
    originalPrice: product.discount_price ? product.retail_price : null,
    image: product.images && product.images.length > 0 ?
      (() => {
        const imagePath = product.images[0].image || product.images[0];
        if (!imagePath) return '/assets/images/no_image.png';
        if (imagePath.startsWith('http')) return imagePath;
        const baseUrl = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8000';
        return `${baseUrl}${imagePath}`;
      })() : '/assets/images/no_image.png',
    categories: product.category ? [product.category.name] : [],
    // Use backend success_rate if available, otherwise fall back to calculated or default
    successRate: product.success_rate || (product.external_rating_average ?
      Math.round(parseFloat(product.external_rating_average) * 20) : 85),
    rating: product.rating || 4.5,
    // Pass through rating fields from backend (with fallback test data)
    // TODO: Remove fallback test data when real ratings are available in backend
    external_rating_average: product.external_rating_average || (Math.random() > 0.3 ? (3.5 + Math.random() * 1.5).toFixed(1) : null),
    external_rating_count: product.external_rating_count || (product.external_rating_average ? Math.floor(Math.random() * 500) + 10 : null),
    shipping: "2-3 days", // Mock shipping for now
    whyItWorks: product.gift_description || product.description || "A great gift option for your loved ones.",
    occasion: "birthday", // Mock occasion for now
    relationship: "friend", // Mock relationship for now
    personalityTraits: ["practical"], // Mock traits for now
    // Add gift context fields from backend
    age_range: product.age_range,
    gender_suitability: product.gender_suitability,
    occasions: product.occasions || [],
    gift_score: product.success_rate ? product.success_rate / 100 : 0.85 // Convert percentage to 0-1 range
  });

  const transformedProducts = products.map(transformProductToGift);

  // Load products when URL parameters change (with debouncing for search)
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If it's a search query, debounce it to avoid multiple calls
    if (currentFilters.search && currentFilters.search.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        loadProducts();
      }, 500); // 500ms debounce for search
    } else {
      // For filters and pagination, load immediately
      loadProducts();
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchParams]); // Use searchParams directly to avoid callback dependency issues

  // Infinite scroll functionality
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only trigger infinite scroll when scrolling down
      const isScrollingDown = currentScrollY > lastScrollY.current;

      // Check if user is near bottom of page (500px threshold)
      const nearBottom = window.innerHeight + currentScrollY >= document.documentElement.offsetHeight - 500;

      // Update last scroll position
      lastScrollY.current = currentScrollY;

      // Show/hide scroll to top button based on scroll position
      // Show after scrolling down more than 800px (roughly after first page)
      setShowScrollToTop(currentScrollY > 800);

      // Only load more when scrolling down, near bottom, and conditions are met
      // Also add a small delay to prevent multiple rapid calls
      if (isScrollingDown && nearBottom && hasNextPage && !loading && !loadingMore) {
        loadMoreProducts();
      }
    };

    // Throttle scroll events for performance
    let timeoutId;
    const throttledHandleScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 100);
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasNextPage, loading, loadingMore]); // Dependencies for the scroll handler

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Handle filter changes (batched - doesn't apply immediately)
  const handleFilterChange = (newFilters) => {
    // Handle special case for search query - apply immediately for better UX
    if (newFilters.hasOwnProperty('search')) {
      const shouldResetPage = !newFilters.hasOwnProperty('page');
      const updatedParams = {
        ...currentFilters,
        ...newFilters,
        ...(shouldResetPage ? { page: 1 } : {})
      };

      // Remove empty values
      Object.keys(updatedParams).forEach(key => {
        if (!updatedParams[key]) delete updatedParams[key];
      });

      setSearchParams(updatedParams);
      return;
    }

    // For other filters, update pending state
    const updatedPendingFilters = {
      ...pendingFilters,
      ...newFilters
    };

    setPendingFilters(updatedPendingFilters);

    // Check if pending filters differ from applied filters
    const filtersChanged = JSON.stringify(updatedPendingFilters) !== JSON.stringify(filters);
    setHasUnappliedFilters(filtersChanged);
  };

  // Apply pending filters to URL and trigger search
  const applyFilters = () => {
    console.log('🚀 APPLYING FILTERS:', pendingFilters);

    const updatedParams = {
      ...currentFilters,
      ...pendingFilters,
      page: 1 // Reset to first page when applying new filters
    };

    // Remove empty values
    Object.keys(updatedParams).forEach(key => {
      const value = updatedParams[key];

      // Remove falsy values (empty strings, null, undefined)
      if (!value) {
        delete updatedParams[key];
        return;
      }

      // Remove empty arrays
      if (Array.isArray(value) && value.length === 0) {
        delete updatedParams[key];
        return;
      }

      // Remove empty customBudget objects
      if (key === 'customBudget' && typeof value === 'object') {
        if (!value.min && !value.max) {
          delete updatedParams[key];
        }
      }
    });

    setSearchParams(updatedParams);
    setFilters(pendingFilters);
    setHasUnappliedFilters(false);

    // Note: loadProducts() will be called automatically by useEffect when searchParams change
  };

  // Load more products for infinite scroll
  const loadMoreProducts = async () => {
    if (loadingMore || !hasNextPage) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      // Prepare API parameters (use current filters but with next page)
      const apiFilters = { ...currentFilters, page: nextPage };

      // Remove empty values
      Object.keys(apiFilters).forEach(key => {
        if (!apiFilters[key]) delete apiFilters[key];
      });

      let data;

      // Determine which API to call based on search query
      const baseSearchQuery = currentFilters.search || '';
      if (baseSearchQuery.trim()) {
        data = await productsService.searchProducts(baseSearchQuery, apiFilters);
      } else {
        data = await productsService.getProducts(apiFilters);
      }

      const paginationData = handlePaginationResponse(data);
      const newProducts = paginationData.results || data.results || data;

      if (newProducts && newProducts.length > 0) {
        // Append new products to existing ones
        setProducts(prevProducts => [...prevProducts, ...newProducts]);

        // Update pagination state
        setHasNextPage(paginationData.hasNext || !!data.next);
        setCurrentPage(nextPage);

        // Note: Don't update URL during infinite scroll to avoid triggering loadProducts
        // which would replace all products instead of appending them
      } else {
        setHasNextPage(false);
      }

    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle product view tracking
  const handleProductView = async (productId) => {
    if (isAuthenticated) {
      try {
        await productsService.recordProductView(productId);
      } catch (error) {
        console.error('Error recording product view:', error);
      }
    }
  };

  // Handle scroll to top
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle navigation to product detail page in new tab
  const handleViewDetails = (productId) => {
    window.open(`/products/${productId}`, '_blank');
  };

  // Handle badge click for filtering
  const handleBadgeClick = (filterType, filterValue) => {
    const filterMapping = {
      'age_range': 'ageRange',
      'gender_suitability': 'gender',
      'occasions': 'occasion'
    };

    const mappedFilter = filterMapping[filterType] || filterType;

    // Apply filter immediately
    handleFilterChange({
      [mappedFilter]: filterValue
    });
  };

  const handleClearFilters = () => {
    // Clear both pending and applied filters
    const emptyFilters = {
      relationship: '',
      gender: '',
      ageRange: '',
      category: '',
      budget: '',
      customBudget: { min: '', max: '' },
      occasion: '',
      interests: [],
      negativeInterests: []
    };

    setPendingFilters(emptyFilters);
    setFilters(emptyFilters);
    setHasUnappliedFilters(false);
    setSearchParams({});
  };

  const handleSaveGift = async (giftId) => {
    if (!isAuthenticated) {
      // Show login prompt or redirect to login
      console.log('User must be logged in to save gifts');
      // TODO: Show login modal or redirect
      return;
    }

    if (!defaultWishlist) {
      console.error('No default wishlist available');
      return;
    }

    setWishlistLoading(true);

    try {
      const isCurrentlySaved = savedGifts.includes(giftId);

      if (isCurrentlySaved) {
        // Remove from wishlist
        await wishlistsService.removeProductFromWishlist(defaultWishlist.id, giftId);
        setSavedGifts(prev => prev.filter(id => id !== giftId));
        console.log('Removed gift from wishlist:', giftId);
      } else {
        // Add to wishlist
        await wishlistsService.addProductToWishlist(defaultWishlist.id, giftId);
        setSavedGifts(prev => [...prev, giftId]);
        console.log('Added gift to wishlist:', giftId);
      }

      // Optionally refresh the wishlist to get updated data
      // await loadUserWishlists();

    } catch (error) {
      console.error('Error saving/removing gift:', error);
      // TODO: Show error message to user
      setError('Failed to save gift. Please try again.');
    } finally {
      setWishlistLoading(false);
    }
  };

  // Enhanced user action handlers
  const handleSaveToContact = async (productId, contactId) => {
    if (!isAuthenticated) {
      console.log('User must be logged in to save gifts to contacts');
      return;
    }

    setActionLoading(prev => ({ ...prev, [`contact_${productId}`]: true }));

    try {
      await saveToContact(productId, contactId);
      console.log('Saved gift to contact:', productId, contactId);
      // TODO: Show success message
    } catch (error) {
      console.error('Error saving gift to contact:', error);
      setError('Failed to save gift to contact. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [`contact_${productId}`]: false }));
    }
  };

  const handleMarkPurchased = async (productId) => {
    if (!isAuthenticated) {
      console.log('User must be logged in to mark purchases');
      return;
    }

    setActionLoading(prev => ({ ...prev, [`purchase_${productId}`]: true }));

    try {
      await markAsPurchased(productId);
      setUserPurchases(prev => [...prev, productId]);
      console.log('Marked as purchased:', productId);
      // TODO: Show success message
    } catch (error) {
      console.error('Error marking as purchased:', error);
      setError('Failed to mark as purchased. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [`purchase_${productId}`]: false }));
    }
  };

  // Share functionality now handled by sophisticated SocialShare component
  // No need for custom share handler since SocialShare handles everything

  const handleOpenWishlistModal = (product) => {
    if (!isAuthenticated) {
      console.log('User must be logged in to save gifts to wishlists');
      return;
    }

    // Map gift data to enhanced product format for the modal
    const enhancedProduct = {
      id: product.id,
      name: product.name,
      retail_price: product.price,
      images: product.images || (product.image ? [{ image: product.image, is_main: true }] : [])
    };

    setSelectedProductForWishlist(enhancedProduct);
    setShowWishlistModal(true);
  };

  const handleWishlistModalClose = () => {
    setShowWishlistModal(false);
    setSelectedProductForWishlist(null);
  };

  const handleAddToWishlist = (wishlist) => {
    console.log('Product added to wishlist:', wishlist.name);
    // Update saved gifts state to reflect the change
    if (selectedProductForWishlist) {
      setSavedGifts(prev => {
        // Avoid duplicates
        if (!prev.includes(selectedProductForWishlist.id)) {
          return [...prev, selectedProductForWishlist.id];
        }
        return prev;
      });
    }
    handleWishlistModalClose();
  };

  const handleOpenShareModal = (product) => {
    // Map gift data to enhanced product format for the share modal
    const enhancedProduct = {
      id: product.id,
      name: product.name,
      current_price: product.price,
      description: product.whyItWorks || product.description,
      images: product.images || (product.image ? [{ image: product.image, is_main: true }] : []),
      slug: product.slug || `product-${product.id}`,
      success_rate: product.successRate || product.gift_score ? Math.round((product.gift_score || 0) * 100) : null,
      brand: product.brand
    };

    setSelectedProductForShare(enhancedProduct);
    setShowShareModal(true);
  };

  const handleShareModalClose = () => {
    setShowShareModal(false);
    setSelectedProductForShare(null);
  };

  const handleRemoveFromWishlist = async (productId) => {
    if (!isAuthenticated) {
      console.log('User must be logged in to manage wishlists');
      return;
    }

    setActionLoading(prev => ({ ...prev, [`wishlist_${productId}`]: true }));

    try {
      // Find all wishlists that contain this product and remove it
      const removePromises = userWishlists.map(async (wishlist) => {
        if (wishlist.items?.some(item => item.product?.id === productId)) {
          await wishlistsService.removeProductFromWishlist(wishlist.id, productId);
        }
      });

      await Promise.all(removePromises);

      // Update saved gifts state
      setSavedGifts(prev => prev.filter(id => id !== productId));

      console.log('Product removed from all wishlists:', productId);
      // TODO: Show success message
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      setError('Failed to remove from wishlist. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [`wishlist_${productId}`]: false }));
    }
  };

  const handleCompareGift = (giftId) => {
    const gift = transformedProducts.find(g => g.id === giftId);
    if (!gift) return;

    setComparisonItems(prev => {
      const exists = prev.find(item => item.id === giftId);
      if (exists) {
        return prev.filter(item => item.id !== giftId);
      } else if (prev.length < 3) {
        return [...prev, gift];
      } else {
        // Replace oldest item if at limit
        return [...prev.slice(1), gift];
      }
    });
  };

  const handleRemoveFromComparison = (giftId) => {
    setComparisonItems(prev => prev.filter(item => item.id !== giftId));
  };

  const handleClearComparison = () => {
    setComparisonItems([]);
  };

  const handleRequestSimilar = async (giftId) => {
    try {
      setLoading(true);

      // Find the current product to get its characteristics
      const currentProduct = transformedProducts.find(p => p.id === giftId);

      if (currentProduct) {
        // Use product characteristics to find similar items
        const similarFilters = {
          gender: currentProduct.gender_suitability,
          ageRange: currentProduct.age_range,
          category: currentProduct.category?.id,
          exclude: giftId // Exclude current product
        };

        // Clear current results and search for similar items
        const similarResults = await productsService.searchProducts('', similarFilters);

        // Update the products with similar results
        setProducts(similarResults.results || []);
        setPagination(similarResults.pagination || null);

        // Update search params to reflect the similar search
        const newParams = new URLSearchParams();
        Object.entries(similarFilters).forEach(([key, value]) => {
          if (value && key !== 'exclude') {
            newParams.set(key, value);
          }
        });
        setSearchParams(newParams);

        // Show success message
        console.log(`Found ${similarResults.results?.length || 0} similar products`);
      }
    } catch (error) {
      console.error('Error fetching similar products:', error);
      setError('Failed to load similar products. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <Helmet>
        <title>Gift Finder - Discover Perfect Gifts | Giftsy Modern</title>
        <meta name="description" content="Find the perfect gift with our AI-powered gift finder. Filter by relationship, personality, budget, and occasion to discover thoughtful gifts that create lasting memories." />
      </Helmet>

      <Header />



      <div className="min-h-screen bg-surface pt-16 lg:pt-20">
        <div className="flex">
          {/* Filter Sidebar */}
          <FilterSidebar
            filters={pendingFilters}
            categories={categories}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onApplyFilters={applyFilters}
            hasUnappliedFilters={hasUnappliedFilters}
            isOpen={showMobileFilters}
            onToggle={() => setShowMobileFilters(!showMobileFilters)}
            activeFiltersCount={
              (() => {
                let count = 0;

                // Count simple string filters
                if (pendingFilters.relationship && pendingFilters.relationship !== '') count++;
                if (pendingFilters.gender && pendingFilters.gender !== '') count++;
                if (pendingFilters.ageRange && pendingFilters.ageRange !== '') count++;
                if (pendingFilters.category && pendingFilters.category !== '') count++;
                if (pendingFilters.budget && pendingFilters.budget !== '') count++;
                if (pendingFilters.occasion && pendingFilters.occasion !== '') count++;

                // Count custom budget
                if (pendingFilters.customBudget &&
                  (pendingFilters.customBudget.min || pendingFilters.customBudget.max)) {
                  count++;
                }

                // Count array filters
                if (pendingFilters.interests && pendingFilters.interests.length > 0) count++;
                if (pendingFilters.negativeInterests && pendingFilters.negativeInterests.length > 0) count++;

                return count;
              })()
            }
          />

          {/* Main Content */}
          <div className="flex-1 lg:ml-0">
            {/* Search Header */}
            <SearchHeader
              searchQuery={currentFilters.search || searchQuery}
              onSearchChange={(query) => {
                setSearchQuery(query);
                setSmartQueryValue(query);
                handleFilterChange({ search: query });
              }}
              sortBy={currentFilters.ordering || sortBy}
              onSortChange={(ordering) => {
                setSortBy(ordering);
                handleFilterChange({ ordering });
              }}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              resultsCount={searchLoading ? 'Searching...' :
                currentFilters.search ?
                  `${transformedProducts.length}+ results found` :
                  (pagination ? pagination.count : transformedProducts.length)
              }
              onToggleFilters={() => setShowMobileFilters(!showMobileFilters)}
              showMobileFilters={showMobileFilters}
            />

            {/* Smart Query Builder - more compact */}
            {showSmartQuery && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <SmartQueryBuilder
                  onQueryChange={handleSmartQueryChange}
                  initialQuery={smartQueryValue}
                  showPreview={true}
                />
              </div>
            )}

            {/* Results Section */}
            <div className="p-4 lg:p-6">
              {/* Error State */}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                  <button
                    onClick={loadProducts}
                    className="ml-4 underline hover:no-underline"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Search Loading State */}
              {searchLoading ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                    <Icon name="Sparkles" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                    AI Gift Search in Progress
                  </h3>
                  <p className="text-accent-600 mb-4">
                    {searchStage || 'Analyzing your request...'}
                  </p>
                  <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                    <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                  </div>
                  <p className="text-sm text-accent-500 mt-4">
                    We're using AI to find the most relevant gifts for your search
                  </p>
                </div>
              ) : loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-300 h-48 rounded-lg mb-4"></div>
                      <div className="h-4 bg-gray-300 rounded mb-2"></div>
                      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : transformedProducts.length > 0 ? (
                <>
                  <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'
                    }`}>
                    {transformedProducts.map((gift) => (
                      <GiftCard
                        key={gift.id}
                        gift={gift}
                        onSave={handleSaveGift}
                        onCompare={handleCompareGift}
                        onRequestSimilar={handleRequestSimilar}
                        onView={() => handleProductView(gift.id)}
                        onViewDetails={() => handleViewDetails(gift.id)}
                        isSaved={savedGifts.includes(gift.id)}
                        isComparing={comparisonItems.some(item => item.id === gift.id)}
                        wishlistLoading={wishlistLoading}
                        isAuthenticated={isAuthenticated}
                        // Enhanced functionality props
                        onSaveToContact={(contactId) => handleSaveToContact(gift.id, contactId)}
                        onMarkPurchased={() => handleMarkPurchased(gift.id)}
                        onOpenWishlistModal={() => handleOpenWishlistModal(gift)}
                        onRemoveFromWishlist={() => handleRemoveFromWishlist(gift.id)}
                        onOpenShareModal={() => handleOpenShareModal(gift)}
                        onBadgeClick={handleBadgeClick}
                        userState={{
                          isPurchased: userPurchases.includes(gift.id),
                          savedContacts: userContacts.filter(c =>
                            c.gift_ideas?.some(gi => gi.product_id === gift.id)
                          ),
                          isActionLoading: {
                            contact: actionLoading[`contact_${gift.id}`] || false,
                            purchase: actionLoading[`purchase_${gift.id}`] || false,
                            wishlist: actionLoading[`wishlist_${gift.id}`] || false
                          }
                        }}
                        enableGiftFeatures={enableGiftFeatures}
                      />
                    ))}
                  </div>

                  {/* Infinite Scroll Loading Indicator */}
                  {loadingMore && (
                    <div className="mt-8 flex justify-center items-center py-8">
                      <div className="flex items-center space-x-3 text-gray-600">
                        <Icon name="Loader2" size={20} className="animate-spin" />
                        <span className="text-sm font-medium">Loading more gifts...</span>
                      </div>
                    </div>
                  )}

                  {/* End of Results Indicator */}
                  {!hasNextPage && !loadingMore && transformedProducts.length > 0 && (
                    <div className="mt-8 flex justify-center items-center py-8">
                      <div className="text-center">
                        <div className="text-gray-400 mb-2">
                          <Icon name="Check" size={24} className="mx-auto" />
                        </div>
                        <p className="text-sm text-gray-600">You've seen all gifts! 🎁</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Try adjusting your filters to discover more options
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Search" size={32} className="text-accent-400" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                    No gifts found
                  </h3>
                  <p className="text-accent-600 mb-6 max-w-md mx-auto">
                    Try adjusting your filters or search terms to discover more gift options.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}


            </div>
          </div>
        </div>

        {/* Comparison Panel */}
        <ComparisonPanel
          comparisonItems={comparisonItems}
          onRemoveFromComparison={handleRemoveFromComparison}
          onClearComparison={handleClearComparison}
          isOpen={showComparison}
          onToggle={() => setShowComparison(!showComparison)}
        />

        {/* Wishlist Selection Modal */}
        <WishlistSelectionModal
          isOpen={showWishlistModal}
          onClose={handleWishlistModalClose}
          product={selectedProductForWishlist}
          userWishlists={userWishlists}
          onAddToWishlist={handleAddToWishlist}
        />

        {/* Share Modal */}
        {showShareModal && selectedProductForShare && (
          <div className="fixed inset-0 z-50">
            <SocialShare
              product={selectedProductForShare}
              className="page-level-share-modal"
              autoOpen={true}
              onClose={handleShareModalClose}
              key={`share-${selectedProductForShare.id}-${Date.now()}`} // Force re-render to open modal
            />
          </div>
        )}

        {/* Scroll to Top Button */}
        <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${showScrollToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
          <button
            onClick={handleScrollToTop}
            className="p-3 bg-primary hover:bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Scroll to top"
            title="Back to top"
          >
            <Icon name="ArrowUp" size={24} className="text-white" />
          </button>
        </div>

      </div>
    </>
  );
};

export default GiftFinder;